<script>
import { mapActions } from "vuex";
import { copy } from "@/shared/utils/string";
import { ShareLinkIcon, LinkedinIcon, SaveIcon, TrophyIcon } from "@easy/ui/icons";
import { ButtonPrimarySm } from "@easy/ui/buttons";

import { certificateTemplatesService } from "@/apps/player/services/certificate-templates.service";
import { RENDER_ROUTE_NAMES } from "@/views/render/render.routes";

export default {
    components: {
        ButtonPrimarySm,
        ShareLinkIcon,
        LinkedinIcon,
        SaveIcon,
        TrophyIcon,
    },
    props: {
        certTemplate: {
            type: Object,
            required: true,
        },
        certContent: {
            type: Object,
            default: () => ({}),
        },
        issuedCert: {
            type: Object,
            required: true,
        },
        player: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isPreview: this.$route.query.preview && this.$route.query.preview === "true",
        };
    },
    computed: {
        playerUuid() {
            return this.$route.params.projectUuid;
        },
        certificateTemplate() {
            return certificateTemplatesService.getPlayerCertTemplate(
                this.certTemplate?.templateName
            ).component;
        },
    },
    methods: {
        ...mapActions("alert", ["showSuccessMessage"]),
        goToBadge() {
            this.$router.push({
                name: RENDER_ROUTE_NAMES.playerBadge,
                params: {
                    projectUuid: this.playerUuid,
                    certificateUuid: this.$route.params.certificateUuid,
                },
            });
        },
        copyThis() {
            copy(
                `${window.location.origin}/players/${this.playerUuid}/certificates/${this.issuedCert.uuid}`
            );
            this.showSuccessMessage({
                title: this.$t("r_shared.copied"),
            });
        },
        shareThis() {
            const badgeLink = `${window.location.origin}/players/${this.playerUuid}/badges/${this.issuedCert.uuid}`;
            const quote = this.$t("r_player.certificate.share_quote", {
                name: this.certContent.courseName,
            });
            const url = "https://www.linkedin.com/shareArticle";
            const params = new URLSearchParams({
                mini: true,
                url: badgeLink,
                title: quote,
                summary: quote,
                source: "Easyplayer",
            });

            window.open(`${url}?${params.toString()}`, "_blank", "width=600,height=400");
        },
        printThis() {
            window.print();
        },
    },
};
</script>

<template>
    <div
        :class="[isPreview ? '' : 'p-6 lg:p-20 print:p-0']"
        :style="{ 'font-family': player.fontFamily }"
    >
        <div
            class="relative w-full max-w-7xl mx-auto rounded-2xl shadow-2xl overflow-hidden print:rounded-none print:shadow-none"
        >
            <component
                :is="certificateTemplate"
                :certContent="certContent"
                :issuedCert="issuedCert"
                :player="player"
            />
        </div>
        <div
            v-if="!isPreview"
            class="mt-12 flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-2 print:hidden"
        >
            <ButtonPrimarySm class="w-full sm:w-auto" @click="shareThis">
                <LinkedinIcon />
                <span>{{ $t("r_player.certificate.add_to_profile") }}</span>
            </ButtonPrimarySm>
            <ButtonPrimarySm class="w-full sm:w-auto" @click="copyThis">
                <ShareLinkIcon />
                <span>{{ $t("r_player.certificate.copy_link") }}</span>
            </ButtonPrimarySm>
            <ButtonPrimarySm class="w-full sm:w-auto" @click="printThis">
                <SaveIcon />
                <span>{{ $t("r_player.certificate.print") }}</span>
            </ButtonPrimarySm>
            <ButtonPrimarySm class="w-full sm:w-auto" @click="goToBadge">
                <TrophyIcon />
                <span>{{ $t("r_player.certificate.badge_link") }}</span>
            </ButtonPrimarySm>
        </div>
    </div>
</template>

<style>
@page {
    size: A4;
    margin: 0;
}
</style>
